/**
 * Service-desk API client.
 *
 * Thin wrappers over `api` — no caching or state here; that is React Query's job in
 * `hooks/useServiceDesk.ts`. Keeping the transport dumb means a hook can be tested by mocking
 * one module and the URL shapes stay greppable against `API_REFERENCE.md`.
 */

import { api } from '@/services/api';
import type {
  AttachmentArchiveScanResult,
  AttachmentArchiveStatus,
  BulkUpdateRequest,
  ChildSyncStatus,
  MappingStatus,
  MirroredEntity,
  ScanResult,
  SourceMap,
  SyncState,
  BulkUpdateResult,
  Company,
  Contact,
  CustomField,
  Asset,
  AssetImportResult,
  AssetType,
  BedrockServiceInfo,
  Burndown,
  DeviceLink,
  DerivedSites,
  NativeImportResult,
  DeviceSourcePolicy,
  MergedIdentity,
  ReconcileResult,
  ServiceProbe,
  UnmatchedDevice,
  KbArticle,
  KbArticleDetail,
  KbCategory,
  KbSuggestion,
  MirrorPurgeResult,
  MirrorSampleResult,
  ClassificationRule,
  Project,
  ProjectDetail,
  ProjectPhaseDetail,
  ProjectTask,
  ProjectTaskDetail,
  ClassificationTestResult,
  Contract,
  DispatchTarget,
  TriggerSource,
  EscalationPath,
  EscalationAssignments,
  EscalationPreview,
  EscalationRungKind,
  EscalationRungWrite,
  Location,
  Page,
  Queue,
  Tag,
  TicketCreate,
  TicketDetail,
  TicketFilters,
  TicketNote,
  TicketPriority,
  TicketStatus,
  TicketSummary,
  TicketType,
  BusinessCalendar,
  RunningTimer,
  SlaPolicy,
  SlaTargetWrite,
  TicketAttachmentList,
  TicketSla,
  TicketTime,
  TimeEntry,
  Timesheet,
  WorkflowDefinition,
  WorkflowGraph,
  WorkflowRun,
  WorkflowValidation,
  TicketChildRecord,
  TicketUpdate,
} from '@/types/servicedesk';

const V1 = '/api/v1';

/**
 * Drop empty filters so they never reach the wire.
 *
 * An empty string is not the same as "unset": `?queue_id=` would be a 422 against a UUID field,
 * and a blank `search` would narrow nothing while busting the query cache on every keystroke.
 */
function clean(params: object): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
}

export async function listTickets(filters: TicketFilters): Promise<Page<TicketSummary>> {
  const { data } = await api.get<Page<TicketSummary>>(`${V1}/tickets`, { params: clean(filters) });
  return data;
}

export async function getTicket(id: string): Promise<TicketDetail> {
  const { data } = await api.get<TicketDetail>(`${V1}/tickets/${id}`);
  return data;
}

export async function createTicket(body: TicketCreate): Promise<TicketDetail> {
  const { data } = await api.post<TicketDetail>(`${V1}/tickets`, body);
  return data;
}

export async function updateTicket(id: string, body: TicketUpdate): Promise<TicketDetail> {
  const { data } = await api.patch<TicketDetail>(`${V1}/tickets/${id}`, body);
  return data;
}

export async function listChildren(id: string): Promise<TicketSummary[]> {
  const { data } = await api.get<TicketSummary[]>(`${V1}/tickets/${id}/children`);
  return data;
}

export async function listNotes(id: string): Promise<TicketNote[]> {
  const { data } = await api.get<TicketNote[]>(`${V1}/tickets/${id}/notes`);
  return data;
}

export async function addNote(
  id: string,
  body: { body: string; is_internal: boolean; kind?: string; mentions?: string[] },
): Promise<TicketNote> {
  const { data } = await api.post<TicketNote>(`${V1}/tickets/${id}/notes`, body);
  return data;
}

export async function bulkUpdate(body: BulkUpdateRequest): Promise<BulkUpdateResult> {
  const { data } = await api.post<BulkUpdateResult>(`${V1}/tickets/bulk`, body);
  return data;
}

export async function listQueues(): Promise<Queue[]> {
  const { data } = await api.get<Queue[]>(`${V1}/queues`);
  return data;
}

export async function listStatuses(): Promise<TicketStatus[]> {
  const { data } = await api.get<TicketStatus[]>(`${V1}/statuses`);
  return data;
}

export async function listPriorities(): Promise<TicketPriority[]> {
  const { data } = await api.get<TicketPriority[]>(`${V1}/priorities`);
  return data;
}

export async function listTypes(): Promise<TicketType[]> {
  const { data } = await api.get<TicketType[]>(`${V1}/ticket-types`);
  return data;
}

export async function listTags(search?: string): Promise<Tag[]> {
  const { data } = await api.get<Tag[]>(`${V1}/tags`, { params: clean({ search }) });
  return data;
}

export async function listCompanies(params: {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
}): Promise<Page<Company>> {
  const { data } = await api.get<Page<Company>>(`${V1}/companies`, { params: clean(params) });
  return data;
}

export async function getCompany(id: string): Promise<Company> {
  const { data } = await api.get<Company>(`${V1}/companies/${id}`);
  return data;
}

export async function createCompany(body: Partial<Company>): Promise<Company> {
  const { data } = await api.post<Company>(`${V1}/companies`, body);
  return data;
}

export async function updateCompany(id: string, body: Partial<Company>): Promise<Company> {
  const { data } = await api.patch<Company>(`${V1}/companies/${id}`, body);
  return data;
}

export async function listCompanyLocations(id: string): Promise<Location[]> {
  const { data } = await api.get<Location[]>(`${V1}/companies/${id}/locations`);
  return data;
}

export async function listContacts(params: {
  page?: number;
  page_size?: number;
  search?: string;
  company_id?: string;
}): Promise<Page<Contact>> {
  const { data } = await api.get<Page<Contact>>(`${V1}/contacts`, { params: clean(params) });
  return data;
}

export async function getContact(id: string): Promise<Contact> {
  const { data } = await api.get<Contact>(`${V1}/contacts/${id}`);
  return data;
}

export async function createContact(body: Partial<Contact>): Promise<Contact> {
  const { data } = await api.post<Contact>(`${V1}/contacts`, body);
  return data;
}

export async function updateContact(id: string, body: Partial<Contact>): Promise<Contact> {
  const { data } = await api.patch<Contact>(`${V1}/contacts/${id}`, body);
  return data;
}

export async function listCustomFieldTypes(): Promise<string[]> {
  const { data } = await api.get<string[]>(`${V1}/custom-fields/types`);
  return data;
}

/** Build the CSV download URL for the current filters (same params as the list endpoint). */
export function exportTicketsUrl(filters: TicketFilters): string {
  const params = new URLSearchParams(
    Object.entries(clean(filters)).map(([key, value]) => [key, String(value)]),
  );
  // page/page_size are meaningless for an export and would only confuse the server-side cap.
  params.delete('page');
  params.delete('page_size');
  return `${V1}/tickets/export?${params.toString()}`;
}

export async function downloadTicketExport(filters: TicketFilters): Promise<Blob> {
  // Fetched through the axios instance rather than as a plain link so the Authorization header
  // is attached — a bare <a href> would hit the endpoint unauthenticated and 401.
  const { data } = await api.get<Blob>(exportTicketsUrl(filters), { responseType: 'blob' });
  return data;
}

export async function listCustomFields(entityType?: string): Promise<CustomField[]> {
  const { data } = await api.get<CustomField[]>(`${V1}/custom-fields`, {
    params: clean({ entity_type: entityType }),
  });
  return data;
}

// ---------------------------------------------------------------------------------------
// Phase 2 — external mirror and company matching
// ---------------------------------------------------------------------------------------

export async function listMirrored(params: {
  service_type?: string;
  resource_type?: string;
  search?: string;
  company_id?: string;
  unmatched_only?: boolean;
  page?: number;
  page_size?: number;
}): Promise<Page<MirroredEntity>> {
  const { data } = await api.get<Page<MirroredEntity>>(`${V1}/mirror`, { params: clean(params) });
  return data;
}

export async function getMirrored(id: string, refresh = false): Promise<MirroredEntity> {
  const { data } = await api.get<MirroredEntity>(`${V1}/mirror/${id}`, {
    params: refresh ? { refresh: true } : {},
  });
  return data;
}

export async function listSyncState(): Promise<SyncState[]> {
  const { data } = await api.get<SyncState[]>(`${V1}/mirror/sync-state`);
  return data;
}

export async function triggerMirrorSync(forceFull = false): Promise<unknown> {
  const { data } = await api.post(`${V1}/mirror/sync`, null, {
    params: clean({ force_full: forceFull || undefined }),
  });
  return data;
}

export async function listSourceMaps(params: {
  service_type?: string;
  status_filter?: MappingStatus;
}): Promise<SourceMap[]> {
  const { data } = await api.get<SourceMap[]>(`${V1}/source-maps`, { params: clean(params) });
  return data;
}

export async function scanSourceMaps(body: {
  service_type?: string;
  /** `companies` for Autotask; `sites` for a device source, whose sites are derived from devices. */
  resource_type?: string;
  auto_confirm_exact_domain?: boolean;
}): Promise<ScanResult> {
  const { data } = await api.post<ScanResult>(`${V1}/source-maps/scan`, body);
  return data;
}

export async function confirmSourceMap(id: string, companyId?: string): Promise<SourceMap> {
  const { data } = await api.post<SourceMap>(`${V1}/source-maps/${id}/confirm`, {
    company_id: companyId ?? null,
  });
  return data;
}

export async function rejectSourceMap(id: string): Promise<SourceMap> {
  const { data } = await api.post<SourceMap>(`${V1}/source-maps/${id}/reject`);
  return data;
}

export async function unlinkSourceMap(id: string): Promise<void> {
  await api.delete(`${V1}/source-maps/${id}`);
}

// ---------------------------------------------------------------------------------------
// Deep-ticket children and attachments (Bedrock 486842f)
// ---------------------------------------------------------------------------------------

export async function listTicketAttachments(ticketId: string): Promise<TicketAttachmentList> {
  const { data } = await api.get<TicketAttachmentList>(
    `${V1}/mirror/tickets/${encodeURIComponent(ticketId)}/attachments`,
  );
  return data;
}

/**
 * Fetch this ticket's attachment metadata from the source on demand — one upstream request.
 *
 * Exists so the bulk walk is optional: without it, every ticket the walk has not reached would show
 * an empty attachment list, which reads as "no attachments" when the truth is "not looked at yet".
 */
export async function checkTicketAttachments(
  ticketId: string,
): Promise<{ ticket_id: string; found: number }> {
  const { data } = await api.post<{ ticket_id: string; found: number }>(
    `${V1}/mirror/tickets/${encodeURIComponent(ticketId)}/attachments/check`,
  );
  return data;
}

export async function listTicketChildren(
  ticketId: string,
  resourceType: string,
): Promise<TicketChildRecord[]> {
  const { data } = await api.get<TicketChildRecord[]>(
    `${V1}/mirror/tickets/${encodeURIComponent(ticketId)}/children/${encodeURIComponent(resourceType)}`,
  );
  return data;
}

export async function listChildSyncStatus(): Promise<ChildSyncStatus[]> {
  const { data } = await api.get<ChildSyncStatus[]>(`${V1}/mirror/tickets/children`);
  return data;
}

export async function getAttachmentArchiveStatus(): Promise<AttachmentArchiveStatus> {
  const { data } = await api.get<AttachmentArchiveStatus>(`${V1}/mirror/attachments/archive`);
  return data;
}

export async function scanAttachmentArchive(): Promise<AttachmentArchiveScanResult> {
  const { data } = await api.post<AttachmentArchiveScanResult>(
    `${V1}/mirror/attachments/archive/scan`,
  );
  return data;
}

/**
 * Download one attachment.
 *
 * Goes through axios rather than a plain `<a href>` for one reason that matters: the endpoint is
 * permission-gated and the token lives in localStorage, not a cookie, so a bare link would arrive
 * unauthenticated and 401. The blob is handed to the browser via a transient object URL, which is
 * revoked immediately — an un-revoked one pins the whole file in memory for the life of the tab,
 * and these are the largest objects this app handles.
 */
export async function downloadAttachment(
  attachmentId: string,
  options: { noteAttachment?: boolean; filename?: string | null } = {},
): Promise<void> {
  const response = await api.get<Blob>(
    `${V1}/mirror/attachments/${encodeURIComponent(attachmentId)}/download`,
    {
      params: clean({ note_attachment: options.noteAttachment || undefined }),
      responseType: 'blob',
      // Attachments are the largest single thing this app moves and are fetched from Autotask on
      // demand; the default 30s turns a big file into a spurious failure.
      timeout: 300_000,
    },
  );

  const url = URL.createObjectURL(response.data);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = options.filename || `attachment-${attachmentId}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---------------------------------------------------------------------------------------
// SLA (§5.6)
// ---------------------------------------------------------------------------------------

export async function getTicketSla(ticketId: string): Promise<TicketSla> {
  const { data } = await api.get<TicketSla>(`${V1}/sla/tickets/${encodeURIComponent(ticketId)}`);
  return data;
}

export async function listSlaPolicies(): Promise<SlaPolicy[]> {
  const { data } = await api.get<SlaPolicy[]>(`${V1}/sla/policies`);
  return data;
}

export async function listBusinessCalendars(): Promise<BusinessCalendar[]> {
  const { data } = await api.get<BusinessCalendar[]>(`${V1}/sla/calendars`);
  return data;
}

/**
 * Replace a policy's per-priority targets.
 *
 * Whole-set replacement, matching the API: a priority left out of the payload means "no promise for
 * this priority", which per-row patching could not express — deletion would become the one thing
 * the editor could not do.
 */
export async function setSlaTargets(
  policyId: string,
  targets: SlaTargetWrite[],
): Promise<SlaPolicy> {
  const { data } = await api.put<SlaPolicy>(
    `${V1}/sla/policies/${encodeURIComponent(policyId)}/targets`,
    targets,
  );
  return data;
}

// ---------------------------------------------------------------------------------------
// Time tracking (§5.4)
// ---------------------------------------------------------------------------------------

export async function getRunningTimer(): Promise<RunningTimer | null> {
  const { data } = await api.get<RunningTimer | null>(`${V1}/time/timer`);
  return data;
}

export async function startTimer(body: {
  ticket_id?: string | null;
  notes?: string | null;
  work_type?: string | null;
  is_billable?: boolean;
}): Promise<RunningTimer> {
  const { data } = await api.post<RunningTimer>(`${V1}/time/timer/start`, body);
  return data;
}

/** Returns null when nothing was running, or when the elapsed time was under a minute. */
export async function stopTimer(notes?: string): Promise<TimeEntry | null> {
  const { data } = await api.post<TimeEntry | null>(
    `${V1}/time/timer/stop`,
    null,
    { params: clean({ notes }) },
  );
  return data;
}

export async function listTimeEntries(params: {
  ticket_id?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  state?: string;
}): Promise<TimeEntry[]> {
  const { data } = await api.get<TimeEntry[]>(`${V1}/time/entries`, { params: clean(params) });
  return data;
}

export async function createTimeEntry(body: {
  ticket_id?: string | null;
  worked_on: string;
  minutes: number;
  notes?: string | null;
  work_type?: string | null;
  is_billable?: boolean;
}): Promise<TimeEntry> {
  const { data } = await api.post<TimeEntry>(`${V1}/time/entries`, body);
  return data;
}

export async function updateTimeEntry(
  id: string,
  body: Partial<{
    worked_on: string;
    minutes: number;
    notes: string | null;
    work_type: string | null;
    is_billable: boolean;
  }>,
): Promise<TimeEntry> {
  const { data } = await api.patch<TimeEntry>(`${V1}/time/entries/${encodeURIComponent(id)}`, body);
  return data;
}

export async function deleteTimeEntry(id: string): Promise<void> {
  await api.delete(`${V1}/time/entries/${encodeURIComponent(id)}`);
}

export async function submitTimeEntries(entryIds: string[]): Promise<{ submitted: number }> {
  const { data } = await api.post<{ submitted: number }>(`${V1}/time/entries/submit`, {
    entry_ids: entryIds,
  });
  return data;
}

export async function approveTimeEntries(entryIds: string[]): Promise<{ approved: number }> {
  const { data } = await api.post<{ approved: number }>(`${V1}/time/entries/approve`, {
    entry_ids: entryIds,
  });
  return data;
}

export async function rejectTimeEntries(
  entryIds: string[],
  reason: string,
): Promise<{ rejected: number }> {
  const { data } = await api.post<{ rejected: number }>(`${V1}/time/entries/reject`, {
    entry_ids: entryIds,
    reason,
  });
  return data;
}

export async function getTimesheet(weekStart: string, userId?: string): Promise<Timesheet> {
  const { data } = await api.get<Timesheet>(`${V1}/time/timesheet`, {
    params: clean({ week_start: weekStart, user_id: userId }),
  });
  return data;
}

export async function getTicketTime(ticketId: string): Promise<TicketTime> {
  const { data } = await api.get<TicketTime>(
    `${V1}/time/tickets/${encodeURIComponent(ticketId)}`,
  );
  return data;
}

// ---------------------------------------------------------------------------------------
// Workflow automation (§5.6)
// ---------------------------------------------------------------------------------------

export async function listWorkflows(): Promise<WorkflowDefinition[]> {
  const { data } = await api.get<WorkflowDefinition[]>(`${V1}/workflows`);
  return data;
}

export async function getWorkflow(id: string): Promise<WorkflowDefinition> {
  const { data } = await api.get<WorkflowDefinition>(`${V1}/workflows/${encodeURIComponent(id)}`);
  return data;
}

/** Saving never changes the lifecycle — publishing is a separate, validated act. */
export async function saveWorkflow(
  id: string,
  body: {
    name: string;
    description?: string | null;
    trigger_type: string;
    graph: WorkflowGraph;
  },
): Promise<WorkflowDefinition> {
  const { data } = await api.put<WorkflowDefinition>(
    `${V1}/workflows/${encodeURIComponent(id)}`,
    body,
  );
  return data;
}

/** New flows are always created as drafts by the server. */
export async function createWorkflow(body: {
  name: string;
  description?: string | null;
  trigger_type: string;
  graph: WorkflowGraph;
}): Promise<WorkflowDefinition> {
  const { data } = await api.post<WorkflowDefinition>(`${V1}/workflows`, body);
  return data;
}

/** Validate a graph without saving, so nodes can be badged as they are configured. */
export async function validateWorkflowGraph(graph: WorkflowGraph): Promise<WorkflowValidation> {
  const { data } = await api.post<WorkflowValidation>(`${V1}/workflows/validate`, graph);
  return data;
}

export async function listWorkflowRuns(params: {
  definition_id?: string;
  entity_id?: string;
  state?: string;
  limit?: number;
}): Promise<WorkflowRun[]> {
  const { data } = await api.get<WorkflowRun[]>(`${V1}/workflows/runs`, { params: clean(params) });
  return data;
}

/** Replay a real past ticket through the graph. Changes nothing. */
export async function dryRunWorkflow(
  id: string,
  ticketId: string,
  graph?: WorkflowGraph,
): Promise<WorkflowRun> {
  const { data } = await api.post<WorkflowRun>(
    `${V1}/workflows/${encodeURIComponent(id)}/dry-run`,
    { ticket_id: ticketId, graph },
  );
  return data;
}

export async function createSlaPolicy(body: {
  name: string;
  description?: string | null;
  calendar_id?: string | null;
  is_default?: boolean;
  is_active?: boolean;
}): Promise<SlaPolicy> {
  const { data } = await api.post<SlaPolicy>(`${V1}/sla/policies`, body);
  return data;
}

export async function updateSlaPolicy(
  id: string,
  body: {
    name: string;
    description?: string | null;
    calendar_id?: string | null;
    is_default?: boolean;
    is_active?: boolean;
  },
): Promise<SlaPolicy> {
  const { data } = await api.put<SlaPolicy>(`${V1}/sla/policies/${encodeURIComponent(id)}`, body);
  return data;
}

export async function createBusinessCalendar(body: {
  name: string;
  description?: string | null;
  timezone: string;
  windows: { weekday: number; start: string; end: string }[];
  holidays: string[];
  is_default?: boolean;
}): Promise<BusinessCalendar> {
  const { data } = await api.post<BusinessCalendar>(`${V1}/sla/calendars`, body);
  return data;
}

export async function updateBusinessCalendar(
  id: string,
  body: {
    name: string;
    description?: string | null;
    timezone: string;
    windows: { weekday: number; start: string; end: string }[];
    holidays: string[];
    is_default?: boolean;
  },
): Promise<BusinessCalendar> {
  const { data } = await api.put<BusinessCalendar>(
    `${V1}/sla/calendars/${encodeURIComponent(id)}`,
    body,
  );
  return data;
}

export async function enableWorkflow(id: string): Promise<WorkflowDefinition> {
  const { data } = await api.post<WorkflowDefinition>(
    `${V1}/workflows/${encodeURIComponent(id)}/enable`,
  );
  return data;
}

export async function disableWorkflow(id: string): Promise<WorkflowDefinition> {
  const { data } = await api.post<WorkflowDefinition>(
    `${V1}/workflows/${encodeURIComponent(id)}/disable`,
  );
  return data;
}

export async function copyWorkflow(id: string): Promise<WorkflowDefinition> {
  const { data } = await api.post<WorkflowDefinition>(
    `${V1}/workflows/${encodeURIComponent(id)}/copy`,
  );
  return data;
}

export async function copySlaPolicy(id: string): Promise<SlaPolicy> {
  const { data } = await api.post<SlaPolicy>(`${V1}/sla/policies/${encodeURIComponent(id)}/copy`);
  return data;
}

export async function copyBusinessCalendar(id: string): Promise<BusinessCalendar> {
  const { data } = await api.post<BusinessCalendar>(
    `${V1}/sla/calendars/${encodeURIComponent(id)}/copy`,
  );
  return data;
}

export async function deleteWorkflow(id: string): Promise<void> {
  await api.delete(`${V1}/workflows/${encodeURIComponent(id)}`);
}

export async function deleteSlaPolicy(id: string): Promise<void> {
  await api.delete(`${V1}/sla/policies/${encodeURIComponent(id)}`);
}

// ---- escalation paths (§5.6, D-128) ----------------------------------------------------

export async function listEscalationRungKinds(): Promise<EscalationRungKind[]> {
  const { data } = await api.get<EscalationRungKind[]>(`${V1}/escalation-paths/rung-kinds`);
  return data;
}

export async function listEscalationPaths(): Promise<EscalationPath[]> {
  const { data } = await api.get<EscalationPath[]>(`${V1}/escalation-paths`);
  return data;
}

export async function getEscalationPath(id: string): Promise<EscalationPath> {
  const { data } = await api.get<EscalationPath>(
    `${V1}/escalation-paths/${encodeURIComponent(id)}`,
  );
  return data;
}

export async function createEscalationPath(body: {
  name: string;
  description?: string | null;
  is_active?: boolean;
  is_default?: boolean;
}): Promise<EscalationPath> {
  const { data } = await api.post<EscalationPath>(`${V1}/escalation-paths`, body);
  return data;
}

export async function updateEscalationPath(
  id: string,
  body: { name: string; description: string | null; is_active: boolean; is_default: boolean },
): Promise<EscalationPath> {
  const { data } = await api.put<EscalationPath>(
    `${V1}/escalation-paths/${encodeURIComponent(id)}`,
    body,
  );
  return data;
}

/**
 * Replace the whole ladder. Wholesale rather than per-rung because a ladder's meaning is its
 * sequence — the server numbers positions from list order, so reordering is a drag rather than a
 * renumbering exercise.
 */
export async function setEscalationRungs(
  id: string,
  rungs: EscalationRungWrite[],
): Promise<EscalationPath> {
  const { data } = await api.put<EscalationPath>(
    `${V1}/escalation-paths/${encodeURIComponent(id)}/rungs`,
    rungs,
  );
  return data;
}

export async function copyEscalationPath(id: string): Promise<EscalationPath> {
  const { data } = await api.post<EscalationPath>(
    `${V1}/escalation-paths/${encodeURIComponent(id)}/copy`,
  );
  return data;
}

export async function deleteEscalationPath(id: string): Promise<void> {
  await api.delete(`${V1}/escalation-paths/${encodeURIComponent(id)}`);
}

/** Where a real ticket stands on its ladder, changing nothing — the escalation dry run. */
export async function previewEscalationPath(
  id: string,
  ticketId: string,
): Promise<EscalationPreview> {
  const { data } = await api.get<EscalationPreview>(
    `${V1}/escalation-paths/${encodeURIComponent(id)}/preview/${encodeURIComponent(ticketId)}`,
  );
  return data;
}

export async function getEscalationAssignments(id: string): Promise<EscalationAssignments> {
  const { data } = await api.get<EscalationAssignments>(
    `${V1}/escalation-paths/${encodeURIComponent(id)}/assignments`,
  );
  return data;
}

/** Replaces what was attached before — a picker can only say "remove" by omission. */
export async function setEscalationAssignments(
  id: string,
  body: EscalationAssignments,
): Promise<EscalationAssignments> {
  const { data } = await api.put<EscalationAssignments>(
    `${V1}/escalation-paths/${encodeURIComponent(id)}/assignments`,
    body,
  );
  return data;
}

/** The events a rung can dispatch, each with the flows that would run. */
export async function listDispatchTargets(): Promise<DispatchTarget[]> {
  const { data } = await api.get<DispatchTarget[]>(`${V1}/escalation-paths/dispatch-targets`);
  return data;
}

/** What fires each workflow trigger — the SLA sweep, and which escalation rungs, by name. */
export async function listTriggerSources(): Promise<TriggerSource[]> {
  const { data } = await api.get<TriggerSource[]>(`${V1}/workflows/trigger-sources`);
  return data;
}

// ---- contracts (§5.4) ------------------------------------------------------------------

export async function listContracts(params: {
  company_id?: string;
  include_inactive?: boolean;
} = {}): Promise<Contract[]> {
  const { data } = await api.get<Contract[]>(`${V1}/contracts`, { params: clean(params) });
  return data;
}

export async function createContract(body: Partial<Contract>): Promise<Contract> {
  const { data } = await api.post<Contract>(`${V1}/contracts`, body);
  return data;
}

export async function updateContract(id: string, body: Partial<Contract>): Promise<Contract> {
  const { data } = await api.put<Contract>(`${V1}/contracts/${encodeURIComponent(id)}`, body);
  return data;
}

export async function deleteContract(id: string): Promise<void> {
  await api.delete(`${V1}/contracts/${encodeURIComponent(id)}`);
}

/** Null when the client has no contract — a normal state, not an error: they are on T&M. */
export async function companyBurndown(companyId: string): Promise<Burndown | null> {
  const { data } = await api.get<Burndown | null>(
    `${V1}/contracts/company/${encodeURIComponent(companyId)}/burndown`,
  );
  return data;
}

// ---- classification rules (§5.25) ------------------------------------------------------

export async function listClassificationRules(): Promise<ClassificationRule[]> {
  const { data } = await api.get<ClassificationRule[]>(`${V1}/classification-rules`);
  return data;
}

export async function createClassificationRule(
  body: Partial<ClassificationRule>,
): Promise<ClassificationRule> {
  const { data } = await api.post<ClassificationRule>(`${V1}/classification-rules`, body);
  return data;
}

export async function updateClassificationRule(
  id: string,
  body: Partial<ClassificationRule>,
): Promise<ClassificationRule> {
  const { data } = await api.put<ClassificationRule>(
    `${V1}/classification-rules/${encodeURIComponent(id)}`,
    body,
  );
  return data;
}

export async function deleteClassificationRule(id: string): Promise<void> {
  await api.delete(`${V1}/classification-rules/${encodeURIComponent(id)}`);
}

/** Whole-list reorder: order is a property of the set, not of one rule. */
export async function reorderClassificationRules(
  ruleIds: string[],
): Promise<ClassificationRule[]> {
  const { data } = await api.put<ClassificationRule[]>(
    `${V1}/classification-rules/order`,
    ruleIds,
  );
  return data;
}

/** Runs the same matcher production runs. Writes nothing. */
export async function testClassification(body: {
  subject?: string;
  body?: string;
  ticket_id?: string;
}): Promise<ClassificationTestResult> {
  const { data } = await api.post<ClassificationTestResult>(
    `${V1}/classification-rules/test`,
    body,
  );
  return data;
}

/** Re-run the rules against one ticket. 409 when it was classified by hand. */
export async function reclassifyTicket(ticketId: string): Promise<TicketDetail> {
  const { data } = await api.post<TicketDetail>(
    `${V1}/tickets/${encodeURIComponent(ticketId)}/reclassify`,
  );
  return data;
}

// ---- projects (§5.2) -------------------------------------------------------------------

export async function listProjects(
  params: { company_id?: string; status?: string; templates?: boolean } = {},
): Promise<Project[]> {
  const { data } = await api.get<Project[]>(`${V1}/projects`, { params: clean(params) });
  return data;
}

export async function getProject(id: string): Promise<ProjectDetail> {
  const { data } = await api.get<ProjectDetail>(`${V1}/projects/${encodeURIComponent(id)}`);
  return data;
}

export async function createProject(body: Partial<Project>): Promise<ProjectDetail> {
  const { data } = await api.post<ProjectDetail>(`${V1}/projects`, body);
  return data;
}

export async function updateProject(
  id: string,
  body: Partial<Project>,
): Promise<ProjectDetail> {
  const { data } = await api.put<ProjectDetail>(`${V1}/projects/${encodeURIComponent(id)}`, body);
  return data;
}

export async function deleteProject(id: string): Promise<void> {
  await api.delete(`${V1}/projects/${encodeURIComponent(id)}`);
}

/** Create a real project from a template, copying its phases and task tree. */
export async function instantiateTemplate(
  templateId: string,
  body: { name: string; company_id?: string | null; lead_id?: string | null },
): Promise<ProjectDetail> {
  const { data } = await api.post<ProjectDetail>(
    `${V1}/projects/${encodeURIComponent(templateId)}/instantiate`,
    body,
  );
  return data;
}

export async function listProjectPhases(projectId: string): Promise<ProjectPhaseDetail[]> {
  const { data } = await api.get<ProjectPhaseDetail[]>(
    `${V1}/projects/${encodeURIComponent(projectId)}/phases`,
  );
  return data;
}

export async function createProjectPhase(
  projectId: string,
  body: { name: string; position?: number },
): Promise<ProjectPhaseDetail> {
  const { data } = await api.post<ProjectPhaseDetail>(
    `${V1}/projects/${encodeURIComponent(projectId)}/phases`,
    body,
  );
  return data;
}

/** Arrives in **tree order** (by materialised path), so `depth` is the indent. */
export async function listProjectTasks(projectId: string): Promise<ProjectTask[]> {
  const { data } = await api.get<ProjectTask[]>(
    `${V1}/projects/${encodeURIComponent(projectId)}/tasks`,
  );
  return data;
}

export async function createProjectTask(
  projectId: string,
  body: Partial<ProjectTask>,
): Promise<ProjectTaskDetail> {
  const { data } = await api.post<ProjectTaskDetail>(
    `${V1}/projects/${encodeURIComponent(projectId)}/tasks`,
    body,
  );
  return data;
}

export async function updateProjectTask(
  taskId: string,
  body: Partial<ProjectTask>,
): Promise<ProjectTaskDetail> {
  const { data } = await api.put<ProjectTaskDetail>(
    `${V1}/projects/tasks/${encodeURIComponent(taskId)}`,
    body,
  );
  return data;
}

export async function deleteProjectTask(taskId: string): Promise<void> {
  await api.delete(`${V1}/projects/tasks/${encodeURIComponent(taskId)}`);
}

// ---- assets (§5.5) ---------------------------------------------------------------------

export async function listAssetTypes(): Promise<AssetType[]> {
  const { data } = await api.get<AssetType[]>(`${V1}/assets/types`);
  return data;
}

export async function listAssets(
  params: {
    company_id?: string;
    asset_type_id?: string;
    status?: string;
    search?: string;
    limit?: number;
  } = {},
): Promise<Asset[]> {
  const { data } = await api.get<Asset[]>(`${V1}/assets`, { params: clean(params) });
  return data;
}

export async function getAsset(id: string): Promise<Asset> {
  const { data } = await api.get<Asset>(`${V1}/assets/${encodeURIComponent(id)}`);
  return data;
}

export async function createAsset(body: Partial<Asset>): Promise<Asset> {
  const { data } = await api.post<Asset>(`${V1}/assets`, body);
  return data;
}

export async function updateAsset(id: string, body: Partial<Asset>): Promise<Asset> {
  const { data } = await api.put<Asset>(`${V1}/assets/${encodeURIComponent(id)}`, body);
  return data;
}

export async function deleteAsset(id: string): Promise<void> {
  await api.delete(`${V1}/assets/${encodeURIComponent(id)}`);
}

/** Warranties ending inside the horizon, soonest first. Expired included by default. */
export async function expiringAssets(
  params: { within_days?: number; company_id?: string; include_expired?: boolean } = {},
): Promise<Asset[]> {
  const { data } = await api.get<Asset[]>(`${V1}/assets/expiring`, { params: clean(params) });
  return data;
}

/**
 * Parse a CSV. With `dry_run: true` nothing is written and the result says what *would* happen —
 * the same endpoint applies it, so the preview cannot disagree with the real thing.
 */
export async function importAssets(
  content: string,
  dryRun: boolean,
): Promise<AssetImportResult> {
  const { data } = await api.post<AssetImportResult>(`${V1}/assets/import`, {
    content,
    dry_run: dryRun,
  });
  return data;
}

export async function assetsForTicket(ticketId: string): Promise<Asset[]> {
  const { data } = await api.get<Asset[]>(`${V1}/assets/ticket/${encodeURIComponent(ticketId)}`);
  return data;
}

export async function linkAssetToTicket(assetId: string, ticketId: string): Promise<void> {
  await api.post(
    `${V1}/assets/${encodeURIComponent(assetId)}/tickets/${encodeURIComponent(ticketId)}`,
  );
}

export async function unlinkAssetFromTicket(assetId: string, ticketId: string): Promise<void> {
  await api.delete(
    `${V1}/assets/${encodeURIComponent(assetId)}/tickets/${encodeURIComponent(ticketId)}`,
  );
}

// ---- mirror maintenance ----------------------------------------------------------------

/**
 * Clear mirrored records so they can be pulled again.
 *
 * Deletes the **mirror** only — never this app's own tickets, companies or contacts. `dryRun` returns
 * the counts; the same call with `false` performs it.
 */
export async function purgeMirror(body: {
  service_type?: string;
  resource_types?: string[];
  reset_cursors?: boolean;
  dry_run: boolean;
}): Promise<MirrorPurgeResult> {
  const { data } = await api.post<MirrorPurgeResult>(`${V1}/mirror/purge`, body);
  return data;
}

/** Import a bounded sample of one resource type, for a test deployment. */
export async function sampleImport(body: {
  service_type?: string;
  resource_type: string;
  limit: number;
  days?: number | null;
}): Promise<MirrorSampleResult> {
  const { data } = await api.post<MirrorSampleResult>(`${V1}/mirror/sample`, body);
  return data;
}

// ---- knowledge base (§5.7) -------------------------------------------------------------

export async function listKbCategories(): Promise<KbCategory[]> {
  const { data } = await api.get<KbCategory[]>(`${V1}/kb/categories`);
  return data;
}

export async function createKbCategory(body: {
  name: string;
  description?: string | null;
}): Promise<KbCategory> {
  const { data } = await api.post<KbCategory>(`${V1}/kb/categories`, body);
  return data;
}

export async function listKbArticles(
  params: { category_id?: string; status?: string; visibility?: string; search?: string } = {},
): Promise<KbArticle[]> {
  const { data } = await api.get<KbArticle[]>(`${V1}/kb`, { params: clean(params) });
  return data;
}

export async function getKbArticle(id: string): Promise<KbArticleDetail> {
  const { data } = await api.get<KbArticleDetail>(`${V1}/kb/${encodeURIComponent(id)}`);
  return data;
}

export async function createKbArticle(body: Partial<KbArticle> & { body?: string }): Promise<KbArticleDetail> {
  const { data } = await api.post<KbArticleDetail>(`${V1}/kb`, body);
  return data;
}

export async function updateKbArticle(
  id: string,
  body: Partial<KbArticle> & { body?: string },
): Promise<KbArticleDetail> {
  const { data } = await api.put<KbArticleDetail>(`${V1}/kb/${encodeURIComponent(id)}`, body);
  return data;
}

/** Move an article through the lifecycle. Separate from editing, and behind `kb.publish`. */
export async function setKbArticleStatus(id: string, status: string): Promise<KbArticleDetail> {
  const { data } = await api.post<KbArticleDetail>(
    `${V1}/kb/${encodeURIComponent(id)}/status`,
    { status },
  );
  return data;
}

export async function deleteKbArticle(id: string): Promise<void> {
  await api.delete(`${V1}/kb/${encodeURIComponent(id)}`);
}

/** Articles that might help with some text. POST, because the text is a ticket body. */
export async function suggestKbArticles(text: string, limit = 5): Promise<KbSuggestion[]> {
  const { data } = await api.post<KbSuggestion[]>(`${V1}/kb/suggest`, { text, limit });
  return data;
}

// ---- device sources and reconciliation (§5.5) ------------------------------------------

/** Every service configured in Bedrock, and what each adapter says it can return. */
export async function listBedrockServices(): Promise<BedrockServiceInfo[]> {
  const { data } = await api.get<BedrockServiceInfo[]>(`${V1}/mirror/services`);
  return data;
}

/** Sample a resource and report which **field names** it carries. Names, never values. */
export async function probeService(
  serviceType: string,
  resourceType = 'devices',
): Promise<ServiceProbe> {
  const { data } = await api.get<ServiceProbe>(`${V1}/mirror/probe`, {
    params: { service_type: serviceType, resource_type: resourceType },
  });
  return data;
}

export async function listUnmatchedDevices(serviceType?: string): Promise<UnmatchedDevice[]> {
  const { data } = await api.get<UnmatchedDevice[]>(`${V1}/assets/devices/unmatched`, {
    params: clean({ service_type: serviceType }),
  });
  return data;
}

export async function reconcileDevices(serviceType?: string): Promise<ReconcileResult> {
  const { data } = await api.post<ReconcileResult>(
    `${V1}/assets/devices/reconcile`,
    null,
    { params: clean({ service_type: serviceType }) },
  );
  return data;
}

/** Create an asset from an unmatched device. Explicit, never automatic. */
export async function promoteDevice(body: {
  mirror_id: string;
  asset_type_id?: string | null;
  company_id?: string | null;
}): Promise<Asset> {
  const { data } = await api.post<Asset>(`${V1}/assets/devices/promote`, body);
  return data;
}

export async function linkDeviceToAsset(mirrorId: string, assetId: string): Promise<DeviceLink> {
  const { data } = await api.post<DeviceLink>(`${V1}/assets/devices/link`, {
    mirror_id: mirrorId,
    asset_id: assetId,
  });
  return data;
}

export async function listAssetDevices(assetId: string): Promise<DeviceLink[]> {
  const { data } = await api.get<DeviceLink[]>(
    `${V1}/assets/${encodeURIComponent(assetId)}/devices`,
  );
  return data;
}

/** Agree with a link, or with its last-user guess — two separate claims. */
export async function confirmDeviceLink(linkId: string, contact = false): Promise<DeviceLink> {
  const { data } = await api.post<DeviceLink>(
    `${V1}/assets/devices/links/${encodeURIComponent(linkId)}/confirm`,
    null,
    { params: { contact } },
  );
  return data;
}

/** What each device source imports and owns. */
export async function listDevicePolicies(): Promise<DeviceSourcePolicy[]> {
  const { data } = await api.get<DeviceSourcePolicy[]>(`${V1}/assets/devices/policies`);
  return data;
}

/** What an asset is called, by whom, and what every other source calls it. */
export async function assetIdentity(assetId: string): Promise<MergedIdentity> {
  const { data } = await api.get<MergedIdentity>(
    `${V1}/assets/${encodeURIComponent(assetId)}/identity`,
  );
  return data;
}

/**
 * Turn mirrored records into this app's own records — the step that makes Autotask tickets appear
 * in the ticket list. Distinct from a mirror *sync*, which only refreshes the raw copies.
 *
 * `dryRun` runs the import in full and rolls it back, so the preview cannot disagree with the real
 * thing.
 */
export async function importMirrorToNative(
  modules: string[],
  dryRun: boolean,
): Promise<NativeImportResult> {
  const { data } = await api.post<NativeImportResult>(`${V1}/mirror/import`, {
    modules,
    dry_run: dryRun,
  });
  return data;
}

/**
 * The client sites a device source names, derived from its mirrored devices.
 *
 * Diagnostic as much as listing: the device count and the keys searched are what make an empty list
 * tell you *why* it is empty.
 */
export async function derivedSites(serviceType: string): Promise<DerivedSites> {
  const { data } = await api.get<DerivedSites>(`${V1}/source-maps/sites`, {
    params: { service_type: serviceType },
  });
  return data;
}

/**
 * Link an external record to a company by hand, with no suggestion to confirm.
 *
 * For sources fuzzy matching cannot serve: an Auvik tenant is a numeric id with no name to score, so
 * it never enters the suggestion queue and could not otherwise be mapped at all.
 */
export async function linkSourceMap(body: {
  service_type: string;
  external_id: string;
  company_id: string;
  external_name?: string | null;
}): Promise<SourceMap> {
  const { data } = await api.post<SourceMap>(`${V1}/source-maps`, body);
  return data;
}
